import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ReportQuery, type Totals, type SourceRow } from '../api';

export function DashboardPage() {
  const [domain, setDomain] = useState('');
  const [domains, setDomains] = useState<string[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [days, setDays] = useState<{ day: string; volume: string; aligned: string; not_aligned: string }[]>([]);
  const [topFailing, setTopFailing] = useState<{ ip: string; header_from: string | null; organisation: string | null; failing: string }[]>([]);
  const [providers, setProviders] = useState<{ provider: string; report_count: number; volume: string }[]>([]);

  const q: ReportQuery = useMemo(() => ({ domain: domain || undefined }), [domain]);

  useEffect(() => {
    api.reportDomains().then((d) => setDomains(d.domains.map((x) => x.domain)));
  }, []);

  useEffect(() => {
    api.reportSummary(q).then((d) => setTotals(d.totals));
    api.reportSources(q).then((d) => setSources(d.sources));
    api.reportVolume(q).then((d) => setDays(d.days));
    api.reportTopFailing(q).then((d) => setTopFailing(d.rows));
    api.reportByProvider(q).then((d) => setProviders(d.providers));
  }, [q]);

  return (
    <>
      <div className="card">
        <h2>DMARC dashboard</h2>
        <div className="flex">
          <select value={domain} onChange={(e) => setDomain(e.target.value)}>
            <option value="">All domains</option>
            {domains.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>

      {totals && (
        <div className="grid-2">
          <div className="card">
            <h2>Totals</h2>
            <dl className="kv">
              <dt>Total volume</dt><dd>{Number(totals.total_volume).toLocaleString()}</dd>
              <dt>DMARC pass</dt><dd>{Number(totals.pass_dmarc).toLocaleString()}</dd>
              <dt>DMARC fail</dt><dd>{Number(totals.fail_dmarc).toLocaleString()}</dd>
              <dt>SPF aligned</dt><dd>{Number(totals.pass_spf).toLocaleString()}</dd>
              <dt>DKIM aligned</dt><dd>{Number(totals.pass_dkim).toLocaleString()}</dd>
            </dl>
          </div>
          <div className="card">
            <h2>Volume over time</h2>
            <Sparkline data={days.map((d) => Number(d.volume))} labels={days.map((d) => d.day)} />
          </div>
        </div>
      )}

      <div className="grid-2">
        <div className="card">
          <h2>Top failing sources</h2>
          {topFailing.length === 0 ? <p className="muted">No failures.</p> : (
            <table>
              <thead><tr><th>IP</th><th>Header-From</th><th>Failing</th></tr></thead>
              <tbody>
                {topFailing.map((r) => (
                  <tr key={r.ip + (r.header_from ?? '')}>
                    <td><Link to={`/source/${encodeURIComponent(r.ip)}`}>{r.ip}</Link></td>
                    <td>{r.header_from ?? '—'}</td>
                    <td style={{ color: 'var(--fail)' }}>{Number(r.failing).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="card">
          <h2>Reports by provider</h2>
          {providers.length === 0 ? <p className="muted">No data.</p> : (
            <table>
              <thead><tr><th>Provider</th><th>Reports</th><th>Volume</th></tr></thead>
              <tbody>
                {providers.map((p) => (
                  <tr key={p.provider}>
                    <td>{p.provider || '(unknown)'}</td>
                    <td>{p.report_count}</td>
                    <td>{Number(p.volume).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Sending sources by IP</h2>
        {sources.length === 0 ? <p className="muted">No data.</p> : (
          <table>
            <thead>
              <tr><th>IP</th><th>Organisation / PTR</th><th>Volume</th><th>Aligned</th><th>Failing</th></tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.ip}>
                  <td><Link to={`/source/${encodeURIComponent(s.ip)}`}>{s.ip}</Link></td>
                  <td className="muted">{s.organisation || s.ptr || '—'}</td>
                  <td>{Number(s.volume).toLocaleString()}</td>
                  <td style={{ color: 'var(--pass)' }}>{Number(s.aligned).toLocaleString()}</td>
                  <td style={{ color: 'var(--fail)' }}>{Number(s.failing).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function Sparkline({ data, labels }: { data: number[]; labels: string[] }) {
  if (data.length === 0) return <p className="muted">No data.</p>;
  const w = 600;
  const h = 80;
  const max = Math.max(...data, 1);
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const path = data
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * step},${h - (v / max) * h}`)
    .join(' ');
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <path d={path} fill="none" stroke="var(--info)" strokeWidth={2} />
      <title>{labels.join(', ')}</title>
    </svg>
  );
}
